/**
 * useCalendarLabels — maps the `calendar` i18n namespace to the
 * presentational `WeeklyCalendarLabels` contract required by `<WeeklyCalendar />`.
 *
 * UX Invariant §9: no hardcoded user-facing strings. The calendar component is
 * domain-agnostic and accepts `labels` as a prop; this hook is the canonical
 * bridge between the i18next store and that prop.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AppointmentStatus,
  ServiceCategory,
  WeeklyCalendarLabels,
} from './types';

const STATUS_KEYS: AppointmentStatus[] = [
  'confirmed',
  'pending',
  'cancelled',
  'no_show',
  'in_progress',
  'completed',
];

const SERVICE_CATEGORY_KEYS: ServiceCategory[] = [
  'grooming',
  'vet',
  'training',
  'boarding',
  'walking',
  'sitting',
  'other',
];

export function useCalendarLabels(): WeeklyCalendarLabels {
  const { t } = useTranslation('calendar');

  return useMemo(() => {
    const statusLabels = STATUS_KEYS.reduce<Record<AppointmentStatus, string>>(
      (acc, key) => {
        acc[key] = t(`statusLabels.${key}`);
        return acc;
      },
      {} as Record<AppointmentStatus, string>,
    );

    const serviceCategoryLabels = SERVICE_CATEGORY_KEYS.reduce<
      Record<ServiceCategory, string>
    >((acc, key) => {
      acc[key] = t(`serviceCategoryLabels.${key}`);
      return acc;
    }, {} as Record<ServiceCategory, string>);

    const dayNames = t('dayNames', { returnObjects: true }) as string[];
    const monthNames = t('monthNames', { returnObjects: true }) as string[];

    return {
      title: t('title'),
      newAppointment: t('newAppointment'),
      prevWeek: t('prevWeek'),
      nextWeek: t('nextWeek'),
      today: t('today'),
      allStaff: t('allStaff'),
      emptySlotHint: t('emptySlotHint'),
      conflictWarning: t('conflictWarning'),
      now: t('now'),
      aiAssistant: t('aiAssistant'),
      filterAll: t('filterAll'),
      weekOf: t('weekOf'),
      noAppointments: t('noAppointments'),
      loading: t('loading'),
      errorTitle: t('errorTitle'),
      closeSheet: t('closeSheet'),
      saveAppointment: t('saveAppointment'),
      cancelAction: t('cancelAction'),
      editAction: t('editAction'),
      markNoShow: t('markNoShow'),
      collectPayment: t('collectPayment'),
      sendReminder: t('sendReminder'),
      customerLabel: t('customerLabel'),
      petLabel: t('petLabel'),
      serviceLabel: t('serviceLabel'),
      staffLabel: t('staffLabel'),
      startLabel: t('startLabel'),
      endLabel: t('endLabel'),
      priceLabel: t('priceLabel'),
      notesLabel: t('notesLabel'),
      vipBadge: t('vipBadge'),
      marketplaceBadge: t('marketplaceBadge'),
      sourceLabel: t('sourceLabel'),
      externalBookingLabel: t('externalBookingLabel'),
      syncAuthorityLockedLabel: t('syncAuthorityLockedLabel'),
      syncAuthorityLockedHelp: t('syncAuthorityLockedHelp'),
      newAppointmentTitle: t('newAppointmentTitle'),
      appointmentDetailsTitle: t('appointmentDetailsTitle'),
      conflictBadge: t('conflictBadge'),
      dayNames: dayNames as WeeklyCalendarLabels['dayNames'],
      monthNames: monthNames as WeeklyCalendarLabels['monthNames'],
      statusLabels,
      serviceCategoryLabels,
    };
  }, [t]);
}
